import { Control, Controller } from 'react-hook-form';

import { ErrorType, Option, RegisterType } from 'types';

import { Headline } from 'components/Headline';

import { usePostCodeSelect } from './PostCodeSelect.hooks';
import * as S from './PostCodeSelect.styles';
import { StyledSelectProps } from './PostCodeSelect.styles';

type SelectProps = {
  options: Option[];
  defaultValue: string;
  control: Control;
  disabled?: boolean;
  city?: string;
  onInputChange?: (arg: string) => void;
  onSelect?: (arg: Option) => void;
  error?: ErrorType;
  required?: boolean;
  className?: string;
  asKeyValues?: boolean;
  ref?: RegisterType;
  controlName?: string;
  isAdditionalTheme?: boolean;
  loading?: boolean;
} & StyledSelectProps;

export function PostCodeSelect(props: SelectProps): React.ReactElement {
  const {
    options,
    defaultValue,
    control,
    disabled = false,
    onInputChange,
    onSelect = undefined,
    scale,
    error = undefined,
    city = '',
    required = false,
    className = undefined,
    asKeyValues = false,
    controlName = 'postCode',
    isAdditionalTheme = false,
    loading = false,
  } = {
    ...props,
  };

  const { selectedValue, selectedValueSet, getStyle } = usePostCodeSelect(
    defaultValue,
    options,
    city,
    control,
    controlName,
    disabled,
    isAdditionalTheme,
    loading
  );

  return (
    <S.Container isAdditionalTheme={isAdditionalTheme} scale={scale} className={className}>
      <S.Header isAdditionalTheme={isAdditionalTheme}>
        <S.Label
          scale={isAdditionalTheme ? 'base' : undefined}
          fontFamily={isAdditionalTheme ? 'LFT Zeno Condensed' : 'LFT Zeno'}
          textTransform={isAdditionalTheme ? 'uppercase' : undefined}
        >
          Post Code
        </S.Label>

        {required ? <Headline color="red">*</Headline> : null}

        {error ? (
          <S.ErrorMessage scale="xs" color="red">
            {error.toString()}
          </S.ErrorMessage>
        ) : null}
      </S.Header>

      <Controller
        control={control}
        defaultValue={defaultValue}
        name={controlName}
        rules={{ required: required && 'Please select postal code.' }}
        render={({ field: { onChange, ref } }) => {
          const setOnChange = (val: Option) => {
            onChange(asKeyValues ? { key: val.value } : val.value);
            selectedValueSet(val);

            if (onSelect) {
              onSelect(val);
            }
          };

          return (
            <S.Select
              styles={getStyle()}
              value={selectedValue}
              isDisabled={disabled}
              options={options}
              onChange={(val: unknown) => setOnChange(val as Option)}
              onInputChange={(e) => {
                if (!onInputChange) return;

                onInputChange(e || '');
              }}
            />
          );
        }}
      />
    </S.Container>
  );
}
